import React, { useState, useEffect } from "react";
import axios from "axios";
// ── FIREBASE AUTHENTICATION IS GONE FROM THIS SCREEN ──
// Changing a password used to mean: re-authenticate with Firebase in the
// browser, call Firebase's updatePassword, then mirror the result into Mongo.
// There is no Firebase Auth account any more, so the whole thing is one
// authenticated request to our own backend — which now verifies the CURRENT
// password itself (it could not before, because Firebase was doing it).
import "./Settings.css";
import Navbar from "./Navbar";
import { getAdPreferences, saveAdPreferences, requestApproxLocation } from "../services/adsApi";
import { getContentPreferences, saveContentPreferences, FALLBACK_OPTIONS } from "../services/preferencesApi";

// One checkbox per option; `selected` keeps the order the user picked them in.
function PreferenceChecklist({ legend, options, selected, onChange, disabled }) {
  const toggle = (value, checked) =>
    onChange(checked ? [...selected.filter((v) => v !== value), value] : selected.filter((v) => v !== value));
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: '0 0 20px' }}>
      <legend style={{ marginBottom: '10px', fontWeight: 600 }}>{legend}</legend>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
        {options.map((option) => (
          <label key={option} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', margin: 0 }}>
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={(e) => toggle(option, e.target.checked)}
              disabled={disabled}
              style={{ width: 'auto', margin: 0 }}
            />
            {option}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Settings() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [email, setEmail] = useState("");
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [msg, setMsg] = useState("");
  // ── ADS ── Personalized Ads switch + optional approximate location.
  const [personalizedAds, setPersonalizedAds] = useState(true);
  const [adLocationNote, setAdLocationNote] = useState("");
  // ── PREFERENCES ── Preferred Languages + Preferred Categories.
  const [preferredLanguages, setPreferredLanguages] = useState([]);
  const [preferredCategories, setPreferredCategories] = useState([]);
  const [contentOptions, setContentOptions] = useState(FALLBACK_OPTIONS);

  const BASE_URL = window.location.hostname === "localhost"
 ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchUserData();
    getAdPreferences()
      .then((data) => {
        setPersonalizedAds(data.personalizedAds !== false);
      })
      .catch(() => {});
    getContentPreferences()
      .then((prefs) => {
        setPreferredLanguages(prefs.preferredLanguages);
        setPreferredCategories(prefs.preferredCategories);
        setContentOptions(prefs.options);
      })
      .catch(() => {});
  }, []);

  // Add class to body for theme
  useEffect(() => {
    document.body.className = theme === 'dark'? 'dark-theme' : 'light-theme';
    localStorage.setItem("theme", theme);
  }, [theme]);

  const fetchUserData = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data.user);
      setName(res.data.user.name || "");
      setBio(res.data.user.bio || "");
      setEmail(res.data.user.email || "");
      if (res.data.user.theme) {
        setTheme(res.data.user.theme);
      }
    } catch (err) {
      setMsg("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setMsg(message);
    setTimeout(() => setMsg(""), 3000);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Send name + bio - name is required by backend
      await axios.put(
        `${BASE_URL}/api/user/profile`,
        { name, bio }, // name add chesanu
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast("Profile updated ✅");
      fetchUserData();
    } catch (err) {
      showToast(err.response?.data?.error || "Update failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword!== confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }
    if (newPassword.length < 6) {
      showToast("Password minimum 6 characters", "error");
      return;
    }
    setLoading(true);
    try {
      // The current password goes to OUR backend, which compares it against the
      // stored bcrypt hash before it writes anything. That check is the reason
      // this is a password change and not an account takeover, and it now lives
      // on the server where it cannot be skipped.
      await axios.put(
        `${BASE_URL}/api/user/change-password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast("Password changed successfully ✅");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      showToast(err.response?.data?.error || err.message || "Password change failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdPreferences = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await saveAdPreferences({ personalizedAds });
      setPersonalizedAds(data.personalizedAds !== false);
      showToast("Ad preferences saved ✅");
    } catch (err) {
      showToast(err.response?.data?.message || "Update failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContentPreferences = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const saved = await saveContentPreferences({ preferredLanguages, preferredCategories });
      setPreferredLanguages(saved.preferredLanguages);
      setPreferredCategories(saved.preferredCategories);
      showToast("Preferences saved ✅");
    } catch (err) {
      showToast(err.response?.data?.error || "Update failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAllowAdLocation = async () => {
    const ok = await requestApproxLocation();
    setAdLocationNote(
      ok
        ? "Approximate location allowed — only your city is used for local ads."
        : "Location not available — ads use your approximate city from your network instead."
    );
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to delete?")) return;
    setLoading(true);
    try {
      // No Firebase account to delete first — the MongoDB record is the whole
      // account now.
      await axios.delete(`${BASE_URL}/api/user/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.clear();
      showToast("Account deleted");
      setTimeout(() => window.location.href = "/login", 2000);
    } catch (err) {
      showToast("Delete failed. Please login again and try", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading &&!user) return <div className="settings-loading">Loading...</div>;

  return (
    <div className="settings-wrapper">
      <Navbar/>
      <div className="settings-card">
        <h1 className="settings-title">⚙️ Settings</h1>

        <div className="settings-tabs">
          <button
            className={activeTab === "profile"? "active" : ""}
            onClick={() => setActiveTab("profile")}
          >
            Profile
          </button>
          <button
            className={activeTab === "security"? "active" : ""}
            onClick={() => setActiveTab("security")}
          >
            Security
          </button>
          <button
            className={activeTab === "ads"? "active" : ""}
            onClick={() => setActiveTab("ads")}
          >
            Ads
          </button>
          <button
            className={activeTab === "preferences"? "active" : ""}
            onClick={() => setActiveTab("preferences")}
          >
            Preferences
          </button>
      {/*   <button
            className={activeTab === "delete"? "active" : ""}
            onClick={() => setActiveTab("delete")}
          >
            Danger Zone
          </button>*/}
        </div>

        {msg && <div className="settings-msg">{msg}</div>}

        {activeTab === "profile" && (
          <form onSubmit={handleUpdateProfile} className="settings-form">
            <h3>Edit Profile</h3>

            {/* NAME - READ ONLY */}
            <label>Name</label>
            <input
              type="text"
              value={name}
              disabled
              style={{ cursor: 'not-allowed', opacity: 0.6 }}
            />
            <small style={{ display: 'block', marginTop: '-15px', marginBottom: '20px', color: '#6b7280' }}>
              Name cannot be changed
            </small>

            {/* EMAIL - READ ONLY */}
            <label>Email</label>
            <input
              type="email"
              value={email}
              disabled
              style={{ cursor: 'not-allowed', opacity: 0.6 }}
            />
            <small style={{ display: 'block', marginTop: '-15px', marginBottom: '20px', color: '#6b7280' }}>
              Email cannot be changed
            </small>
<label>Bio</label>

<textarea
  value={bio}
  onChange={(e) => {
    if (e.target.value.length <= 200) {
      setBio(e.target.value);
    }
  }}
  placeholder="Tell about yourself"
  rows={3}
/>

<p style={{ fontSize: "12px", color: "#666" }}>
  {bio.length}/200
</p>

            <button type="submit" disabled={loading}>
              {loading? "Saving..." : "Save Changes"}
            </button>
          </form>
        )}

        {activeTab === "security" && (
          <div className="settings-form">
            <h3>Change Password</h3>
            <form onSubmit={handleChangePassword}>
              <label>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current password"
                required
              />
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                minLength={6}
                required
              />
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
              <button type="submit" disabled={loading}>
                {loading? "Updating..." : "Change Password"}
              </button>
            </form>
          </div>
        )}

        {activeTab === "ads" && (
          <form onSubmit={handleSaveAdPreferences} className="settings-form">
            <h3>Ads</h3>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={personalizedAds}
                onChange={(e) => setPersonalizedAds(e.target.checked)}
                style={{ width: 'auto', margin: 0 }}
              />
              Personalized Ads: {personalizedAds ? "ON" : "OFF"}
            </label>
            <small style={{ display: 'block', marginTop: '8px', marginBottom: '20px', color: '#6b7280' }}>
              {personalizedAds
                ? "ON — the categories you read, like, save and search may be used to show you more relevant ads."
                : "OFF — your activity is not used for ads. Ads are chosen from the content you are viewing and general advertising, and your learned ad interests are deleted."}
            </small>

            <label>City for local ads</label>
            <button type="button" onClick={handleAllowAdLocation} disabled={loading}>
              Use my approximate location
            </button>
            <small style={{ display: 'block', marginTop: '8px', marginBottom: '20px', color: '#6b7280' }}>
              {adLocationNote ||
                "Optional. Local ads use only your approximate city — from your device location if you allow it, otherwise from your network. Your exact location is never stored, tracked or shown to advertisers."}
            </small>

            <button type="submit" disabled={loading}>
              {loading? "Saving..." : "Save Ad Preferences"}
            </button>
          </form>
        )}

        {activeTab === "preferences" && (
          <form onSubmit={handleSaveContentPreferences} className="settings-form">
            <h3>Reading Preferences</h3>

            <PreferenceChecklist
              legend="Preferred Languages"
              options={contentOptions.languages}
              selected={preferredLanguages}
              onChange={setPreferredLanguages}
              disabled={loading}
            />
            <PreferenceChecklist
              legend="Preferred Categories"
              options={contentOptions.categories}
              selected={preferredCategories}
              onChange={setPreferredCategories}
              disabled={loading}
            />
            <small style={{ display: 'block', marginBottom: '20px', color: '#6b7280' }}>
              Your feed shows more blogs in these languages and categories — nothing else is hidden.
              Searches show blogs in your languages unless you search for a specific language.
              Leave everything unticked for no preference.
            </small>

            <button type="submit" disabled={loading}>
              {loading? "Saving..." : "Save Preferences"}
            </button>
          </form>
        )}

        {activeTab === "delete" && (
          <div className="settings-form">
            <h3 style={{color: '#ef4444'}}>Delete Account</h3>
            <p>
              Deleting your account will permanently removed Account!
            </p>
            <button
              className="danger-btn"
              onClick={handleDeleteAccount}
              disabled={loading}
            >
              {loading? "Deleting..." : "Delete My Account Permanently"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;